const CHARGE_PERIODS = new Set([
  'monthly',
  'quarterly',
  'semi_annual',
  'annual',
  'biennial',
]);

const SUBSCRIPTION_PERIODS = CHARGE_PERIODS;

const NOT_IN_FORCE_STATUSES = new Set([
  'cancelled',
  'suspended',
  'on_hold',
]);

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (value) => {
  const date = toDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const addDays = (value, days) => {
  const date = startOfDay(value);
  if (!date) return null;
  date.setDate(date.getDate() + days);
  return date;
};

export function billingPeriodKey(service) {
  return String(service?.billing_period || '').toLowerCase();
}

export function isSubscriptionService(service) {
  return Boolean(
    SUBSCRIPTION_PERIODS.has(billingPeriodKey(service)) &&
    service?.start_date &&
    service?.end_date
  );
}

function yearOfOneTime(service) {
  const start = toDate(service?.start_date);
  if (start) return start.getFullYear();
  const created = toDate(service?.created_at);
  return created ? created.getFullYear() : null;
}

function subscriptionOverlapsYear(service, year) {
  const start = toDate(service?.start_date);
  const end = toDate(service?.end_date);
  if (!start || !end) return false;
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31, 23, 59, 59);
  return start <= yearEnd && end >= yearStart;
}

function isInForceNow(service, today = new Date()) {
  if (!isSubscriptionService(service)) return false;
  if (NOT_IN_FORCE_STATUSES.has(String(service.status || '').toLowerCase())) return false;
  const start = toDate(service.start_date);
  const end = toDate(service.end_date);
  if (!start || !end) return false;
  return start <= today && end >= today;
}

function matchesValidity(service, validity, today) {
  if (!validity || validity === 'all') return true;

  const endDay = startOfDay(service.end_date);
  const todayStart = startOfDay(today);
  if (validity === 'in_force') return isInForceNow(service, today);
  if (validity === 'expired') {
    return isSubscriptionService(service) && endDay && endDay < todayStart;
  }
  if (!endDay) return false;
  if (validity === 'expiring_today') return endDay.getTime() === todayStart.getTime();
  if (validity === 'expiring_7_days') return endDay > todayStart && endDay <= addDays(today, 7);
  if (validity === 'expiring_30_days') return endDay > todayStart && endDay <= addDays(today, 30);
  if (validity === 'expiring_60_days') return endDay > todayStart && endDay <= addDays(today, 60);
  if (validity === 'expiring_90_days') return endDay > todayStart && endDay <= addDays(today, 90);
  return true;
}

export function filterServicesForListing(services, { chargeFilter = 'all', years = [], validity = 'all' } = {}) {
  if (!Array.isArray(services)) return [];
  const selectedYears = (years || []).map(Number).filter((year) => !Number.isNaN(year));
  const today = new Date();

  return services.filter((service) => {
    const isSub = isSubscriptionService(service);
    const period = billingPeriodKey(service);

    if (chargeFilter === 'one_time' && isSub) return false;
    if (chargeFilter === 'subscription' && !isSub) return false;
    if (CHARGE_PERIODS.has(chargeFilter) && (!isSub || period !== chargeFilter)) return false;

    if (!matchesValidity(service, validity, today)) return false;

    if (selectedYears.length > 0) {
      const matchesYear = isSub
        ? selectedYears.some((year) => subscriptionOverlapsYear(service, year))
        : selectedYears.includes(yearOfOneTime(service));
      if (!matchesYear) return false;
    }

    return true;
  });
}

export function computeListingStatistics(services) {
  const list = Array.isArray(services) ? services : [];
  const rolling = list.filter((service) => service.status !== 'cancelled');
  const netCost = rolling.reduce((sum, service) => sum + Number(service.price || 0), 0);
  const totalCost = rolling.reduce(
    (sum, service) => sum + Number(service.target_amount ?? service.price ?? 0),
    0
  );
  const totalPaid = rolling.reduce((sum, service) => sum + Number(service.paid_amount || 0), 0);

  return {
    total_services: list.length,
    total_credit: netCost,
    total_cost_with_vat: totalCost,
    net_cost: netCost,
    total_paid: totalPaid,
    balance: totalCost - totalPaid,
  };
}
